<template>
  <div class="nominee" redesign>
    <!-- Header -->
    <div class="local-hero local-hero-dark">
      <h1 class="local-hero-header dark">{{ organizationDetails.programName }}</h1>
    </div>

    <div class="form-container">
      <LoadSpinner v-if="loading" :color="`#${organizationDetails.darkBrandColor}`" />
      <template v-if="!loading">
        <div class="cta-section-wrapper">
          <!-- Start User Application Status Section -->

          <!-- User has not completed their application -->
          <template v-if="applicationStarted">
            <div class="cta-section">
              <h2 v-translate>Action Required</h2>
              <h4 v-translate>
                In order to complete your application for enrollment into the Upward Fund, we require you to provide additional information.
              </h4>

              <!-- Complete onboarding button -->
              <button @click="handleCompleteOnboarding" class="form-button dark">
                <LoadSpinner v-if="loading" color="black" />
                <span v-else v-translate>Complete My Enrollment</span>
              </button>
            </div>
          </template>

          <!-- User's application is pending -->
          <template v-else-if="applicationSubmitted">
            <div class="card-shape status-message submitted">
              <div class="success-circle-check-icon">
                <img src="@/assets/icons/success-circle-check-icon.png" />
              </div>
              <div>
                <h4 v-translate>Application Submitted</h4>
                <p v-translate>
                  Your application is being reviewed, please check back soon.
                </p>
              </div>
            </div>
          </template>

          <!-- User's application was denied -->
          <template v-else-if="applicationDeclined">
            <div class="card-shape status-message denied">
              <div class="failure-circle-x-icon">
                <img src="@/assets/icons/error-icon.png" />
              </div>
              <div>
                <h4 v-translate>Application Denied</h4>
                <p v-translate>
                  We regret to inform you that your application was denied. Please check your e-mail for more details.
                </p>
              </div>
            </div>
          </template>

          <!-- End User Application Status Section -->

          <!-- User has completed onboarding, show the normal nominee home page -->
          <template v-else>
            <div class="cta-section">
              <h2 v-translate>Your Monthly Stipend</h2>

              <h4>
                <translate>You have been nominated by</translate>
                <span class="nominated-by"> {{ user.nominatedByFullName }} </span><span> ({{ user.nominatorSubOrganization }}) </span><translate>for a year's worth of
                monthly stipends from The Change Reaction. However, we still need to confirm certain
                details each month to release the money we have set aside for you.</translate>
              </h4>

              <!-- Start or continue stipend request button -->
              <SubmitButton
                :onContinue="handleStartStipendRequest"
                :buttonText="hasStartedStipendRequest ? $gettext('Continue') : $gettext('Get Started')"
                :staticWidth="true"
                :darkText="true"
                :disabled="disableGetStarted"
              />

              <!-- Start Status Messages Section -->

              <!-- User has a declined request -->
              <template v-if="hasDeclinedStipendRequest">
                <div class="card-shape no-longer-enrolled">
                  <p v-translate>You are no longer enrolled in the Upward Fund. Please check your e-mail for more details.</p>
                </div>
              </template>

              <!-- User has started a stipend request -->
              <template v-if="hasStartedStipendRequest">
                <div class="card-shape pending-request">
                  <p v-translate>You have already started a request for this month, click &ldquo;Continue&rdquo; to pick up where you left off.</p>
                </div>
              </template>

              <!-- User has submitted a request that we are reviewing still -->
              <template v-else-if="hasSubmittedStipendRequest">
                <div class="card-shape pending-request">
                  <p v-translate>We are currently reviewing your last request, please check back soon.</p>
                </div>
              </template>

              <!-- User has already submitted a stipend request this month -->
              <template v-else-if="hasApprovedThisMonth">
                <div class="card-shape already-approved">
                  <p v-translate>Your request has already been approved for this month, please check back next month to start your next request.</p>
                </div>
              </template>

              <!-- User needs course codes entered, or the program is over -->
              <template v-else-if="!isEligible">
                <div class="card-shape courses-needed">
                  <!-- User has completed the program -->
                  <template v-if="user.currentProgramMonth > 12">
                    <p v-translate>
                      You have already received your year's worth of monthly stipends. Thank you for participating in the program.
                    </p>
                  </template>

                  <!-- User needs course codes entered -->
                  <template v-else>
                    <p v-translate>
                      Before you can get started on your next request, you must submit completion codes for the following finanical literacy course(s):
                    </p>
                    <ul><li v-for="course in coursesNeeded" class="course-needed">{{ $gettext(course) }}</li></ul>
                  </template>
                </div>
              </template>

              <!-- End Status Messages Section -->
            </div>

            <!-- Start Financial Literacy Course Code Section -->

            <div class="cta-section">
              <h2 v-translate>Financial Literacy Courses</h2>
              <h4 v-translate>
                As part of this program, we ask that you complete 3 financial literacy courses by
                month 4. Please enter your class completion codes below:
              </h4>
              <div class="course-codes">

                <!-- Class 1 course code -->
                <div v-if="!hasFirstCode" class="already-submitted course-code">
                  <input
                    v-model="courseCode[0]"
                    class="form-field-text"
                    type="text"
                    :placeholder="$gettext('Class 1')"
                  />
                  <button @click="handleAddCourseCode(0)" class="form-button dark">
                    <LoadSpinner v-if="courseLoading[0]" color="black" />
                    <span v-else v-translate>Enter</span>
                  </button>
                </div>
                <div v-else class="course-code complete">
                  <span><translate>Class 1 submitted</translate>...</span>
                </div>

                <!-- Class 2 course code -->
                <div v-if="!hasSecondCode" class="course-code">
                  <input
                    v-model="courseCode[1]"
                    class="form-field-text"
                    type="text"
                    :placeholder="$gettext('Class 2')"
                  />
                  <button @click="handleAddCourseCode(1)" class="form-button dark">
                    <LoadSpinner v-if="courseLoading[1]" color="black" />
                    <span v-else v-translate>Enter</span>
                  </button>
                </div>
                <div v-else class="course-code complete">
                  <span><translate>Class 2 submitted</translate>...</span>
                </div>

                <!-- Class 3 course code -->
                <div v-if="!hasThirdCode" class="course-code">
                  <input
                    v-model="courseCode[2]"
                    class="form-field-text"
                    type="text"
                    :placeholder="$gettext('Class 3')"
                  />
                  <button @click="handleAddCourseCode(2)" class="form-button dark">
                    <LoadSpinner v-if="courseLoading[2]" color="black" />
                    <span v-else v-translate>Enter</span>
                  </button>
                </div>
                <div v-else class="course-code complete">
                  <span><translate>Class 3 submitted</translate>...</span>
                </div>

              </div>
            </div>

            <!-- End Financial Literacy Course Code Section -->
          </template>
        </div>
      </template>
    </div>

    <!-- Start stipend request modal -->
    <Modal
      v-if="showGetStartedModal && !hasStartedStipendRequest"
      dimmed
      :additionalModalStyles="{
        'max-width': '500px',
        border: 'none',
        padding: 0,
        overflow: 'hidden scroll',
      }"
      :additionalContainerStyles="{
        zIndex: '1001',
        padding: 0,
      }"
      :width="95"
      @close-modal="showGetStartedModal = false"
    >
      <div class="form-container card-shape modal-window">
        <h2 class="form-field-header" v-translate>Start Your Next Stipend Request</h2>
        <div class="form-field" style="margin-bottom: 0;">
          <p v-translate>You are eligible to begin your next monthly stipend request. Would you like to get started now?</p>
        </div>
        <div class="form-footer">
          <div class="button-container" style="justify-content: space-between;">
            <button class="form-button white-button" @click="showGetStartedModal = false" v-translate>No, I'll start it later</button>
            <SubmitButton
              :onContinue="handleStartStipendRequest"
              :buttonText="$gettext('Get Started')"
              :staticWidth="true"
              :darkText="true"
              :noBottomMargin="true"
            />
          </div>
        </div>
      </div>
    </Modal>
  </div>
</template>

<script>
import { mapState } from 'vuex'

import { LoadSpinner } from '@/components'
import SubmitButton from '@/components/requests/SubmitButton'
import Modal from '@/components/Modal'

import Employee from '@/services/employees'
import NomineesApi from '@/services/nominees'
import SurveyResponse from '@/services/surveyresponses'
import StipendRequest from '@/services/stipendRequests'

export default {
  name: 'NomineeHome',
  components: {
    LoadSpinner,
    Modal,
    SubmitButton,
  },
  data() {
    return {
      courseCodes: [null, null, null],
      courseCode: ['', '', ''],
      courseLoading: [false, false, false],
      loading: true,
      applicationStatus: null,
      showGetStartedModal: false,
    }
  },
  async created() {
    // Get the user's first request to make sure they've onboarded
    const firstStipendRequest = await NomineesApi.createOrGetStipendRequest({ program_month: 1 })
    this.applicationStatus = firstStipendRequest.status
  },
  async mounted() {
    await this.updateUser()
    this.loading = false
  },
  methods: {
    async updateUser() {
      const employee = await Employee.api.retrieve(this.user.id)
      this.$store.dispatch('updateUser', employee)

      this.courseCodes = employee.financialLiteracyCourseCodes

      if(this.isEligible && !this.disableGetStarted)
        this.showGetStartedModal = true
    },
    async handleAddCourseCode(courseIndex) {
      // Check for valid course number
      if (!(courseIndex >= 0 && courseIndex < this.courseCodes.length)) return false

      // Turn on the loading spinner
      this.courseLoading[courseIndex] = true

      // Validate the course code
      let courseNumber = courseIndex + 1
      let code = this.courseCode[courseIndex]
      if (!code) return false

      // Add the code via the NomineesAPI
      let result = await NomineesApi.addCourseCode(courseNumber, code)

      // Re-load the user, so the UI updates
      await this.updateUser()

      // Turn off the loading spinner and return the result
      this.courseLoading[courseIndex] = false

      if(this.isEligible && !has)
        this.showGetStartedModal = true

      return result.success
    },
    async handleStartStipendRequest() {
      const stipendRequest = await NomineesApi.createOrGetStipendRequest({ program_month: this.user.currentProgramMonth })
      this.$store.dispatch('updateCurrentRequest', stipendRequest)
      return true
    },
    handleCompleteOnboarding() {
      // The default route
      this.$router.push({ name: 'NomineeOnboardingImpactStatement' })
    },
  },
  computed: {
    ...mapState(['user', 'organizationDetails', 'organizationCssVars', 'currentRequest']),
    isEligible() {
      if (!this.user) return false

      let currentProgramMonth = this.user.currentProgramMonth
      let courseCodes = this.courseCodes

      // If we're at month two or greater, the first class code must be entered
      if (currentProgramMonth >= 2 && !this.courseCodes[0]) return false

      // If we're at month three or greater, the second class code must be entered
      if (currentProgramMonth >= 3 && !this.courseCodes[1]) return false

      // If we're at month four or greater, the third class code must be entered
      if (currentProgramMonth >= 4 && !this.courseCodes[2]) return false

      // If we're past month 12, the user has completed their year's worth of stipends
      if (currentProgramMonth > 12) return false

      // If we've made it here, the user is caught up on course codes & is eligible
      return true
    },
    hasFirstCode() {
      return this.courseCodes[0] !== null
    },
    hasSecondCode() {
      return this.courseCodes[1] !== null
    },
    hasThirdCode() {
      return this.courseCodes[2] !== null
    },
    coursesNeeded() {
      if (!this.user) return ''

      let currentProgramMonth = this.user.currentProgramMonth
      let courseCodes = this.courseCodes
      let arr = []

      // Add the course names that we need completion codes for
      if (currentProgramMonth >= 2 && !courseCodes[0]) arr.push('Class 1')
      if (currentProgramMonth >= 3 && !courseCodes[1]) arr.push('Class 2')
      if (currentProgramMonth >= 4 && !courseCodes[2]) arr.push('Class 3')

      // Return a commma delimited list of course names for which codes
      // are required
      return arr
    },
    hasApprovedThisMonth() {
      if (!this.user) return true

      return this.user.hasApprovedThisMonth
    },
    hasStartedStipendRequest() {
      if (!this.user) return true

      return this.user.hasStartedStipendRequest
    },
    hasSubmittedStipendRequest() {
      if (!this.user) return true

      return this.user.hasSubmittedStipendRequest
    },
    hasDeclinedStipendRequest() {
      if (!this.user) return true

      return this.user.hasDeclinedStipendRequest
    },
    applicationStarted() {
      return this.applicationStatus === StipendRequest.options.STARTED
    },
    applicationSubmitted() {
      return this.applicationStatus === StipendRequest.options.SUBMITTED
    },
    applicationDeclined() {
      return this.applicationStatus === StipendRequest.options.DECLINED
    },
    disableGetStarted() {
      return this.hasApprovedThisMonth || this.hasSubmittedStipendRequest || this.hasDeclinedStipendRequest || !this.isEligible
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/styles/mixins/responsive';

.nominee {
  .cta-section-wrapper {
    display: flex;
    flex-direction: column;
    justify-content: space-around;
    align-items: center;
    margin: 0rem 1rem;

    @include desktop-screen {
      flex-direction: row;

      .cta-section {
        width: 50%;

        &:nth-child(2n+1) {
          margin-right: 2rem;
        }

        &:nth-child(2n) {
          margin-left: 2rem;
        }
      }
    }

    .cta-section {
      display: flex;
      flex-direction: column;
      align-items: center;
      text-align: center;
      align-self: center;
      margin: 1.5rem 0;
      width: 100%;
      max-width: 500px;
      justify-content: space-between;

      @include desktop-screen {
        align-self: flex-start;
      }

      h1 {
        font-size: 1.5rem;
        font-weight: bold;
        line-height: 2rem;
      }

      h4 {
        font-size: 1.25rem;
        line-height: 1.75rem;
        margin-bottom: 2rem;
      }

      h5 {
        margin-bottom: 0;

        span {
          font-weight: 500;
          font-size: 1rem;
          line-height: 1.5rem;
        }
      }
    }
  }

  .nominated-by {
    font-weight: bold;
  }

  .button-container {
    margin-bottom: 1.5rem;
  }

  .card-shape {
    p:last-of-type {
      margin-bottom: 0;
    }

    &.status-message {
      display: flex;
      width: 60%;
    }

    &.submitted {
      flex-direction: row;
      background-color: #e6ffd9;
    }

    &.denied {
      flex-direction: row;
      background-color: #fcfaf7;
    }

    .success-circle-check-icon, .failure-circle-x-icon {
      margin-right: 1rem;
    }

    &.already-approved, &.courses-needed, &.pending-request, &.no-longer-enrolled {
      margin-top: 1rem;

      @include tablet-and-up-screen {
        padding: 2rem 3rem;
      }
      
      p {
        font-weight: bold;
      }
    }

    &.pending-request {
      background-color: var(--light-brand-color);
      border: 1px solid var(--dark-brand-color);
    }

    &.already-approved {
      background-color: #e8ffee;
      border: 1px solid #a2dbb1;
    }

    &.courses-needed, &.no-longer-enrolled {
      background-color: #faeae8;
      border: 1px solid red;

      ul {
        display: flex;
        flex-direction: column;
        align-items: center;
        padding-left: 0;

        li {
          text-align: left;
          margin-left: 0;
        }
      }
    }
  }

  .course-codes {
    display: flex;
    flex-direction: column;
    width: 100%;
    max-width: 400px;

    @include tablet-and-up-screen {
      max-width: unset;
    }

    .course-code {
      display: flex;
      flex-direction: row;
      margin-bottom: 1.5rem;

      button {
        margin-left: 1rem;
      }

      @include tablet-and-up-screen {
        button {
          width: 160px;
        }
      }

      &.complete {
        color: #5c5c5c;
        justify-content: center;
        font-style: italic;
      }
    }
  }
}
</style>
